import { useRouter } from 'next/router';

import { isNotNullish } from '@tager/web-core';

import { CarsStockType, OptionType } from '@/typings/common';
import useSettingItem from '@/hooks/useSettingItem';
import {
  sortingData,
  TabsPanelLabels,
  TabsPanelLinks,
} from '@/modules/CarsStock/components/common.constants';
import { getFirstDateOfYear, getLastDateOfYear } from '@/utils/date';
import { useTypedSelector } from '@/store/store';
import { selectDealers } from '@/store/reducers/dealers';
import { selectSeoTemplate } from '@/store/reducers/seo-template';
import {
  getSelectedFilters,
  getStockCarList,
  getStockFilterParams,
} from '@/store/reducers/cars-stock';
import {
  getEventMicromarking,
  getSeoTemplateText,
  getStockAndCatalogProductMicromarking,
} from '@/utils/seo';
import { usePageUrl } from '@/hooks/usePageUrl';
import { useCarSelectionHelper } from '@/hooks/useCarSelectionHelper';
import { getAverageRatingAndCountReviewsDealers } from '@/modules/Dealers/helpers';

import { convertSeoField, getStockCarsMinAndMaxPrices } from './common.helpers';

export function useStockCarsSelectOptions(
  carsType: CarsStockType
): OptionType[] {
  const type =
    carsType === 'new'
      ? 'NEW'
      : carsType === 'amp'
      ? 'AMP'
      : carsType === 'electric'
      ? 'ELECTRIC'
      : carsType === 'commercial'
      ? 'COMMERCIAL'
      : carsType === 'hybrid'
      ? 'HYBRID'
      : 'ALL';

  const options = useSettingItem(`STOCK_${type}_SORT_LIST`) ?? [];

  const defaultOption = useSettingItem(`STOCK_${type}_INITIAL_SORT`) ?? '';

  return sortingData
    .filter((option) => options.includes(option.value))
    .sort((option) => (option.value === defaultOption ? -1 : 1));
}

export function useStockCarsTabs(carsType: CarsStockType) {
  const newCarsEnabled = useSettingItem('STOCK_NEW_ENABLED');
  const ampCarsEnabled = useSettingItem('STOCK_AMP_ENABLED');
  const electricCarsEnabled = useSettingItem('STOCK_ELECTRIC_ENABLED');
  const commercialCarsEnabled = useSettingItem('STOCK_COMMERCIAL_ENABLED');
  const hybridCarsEnabled = useSettingItem('STOCK_HYBRID_ENABLED');
  const allCarsEnabled = useSettingItem('STOCK_ALL_ENABLED');

  const activeTab =
    (carsType === 'new' && TabsPanelLabels.NEW_CARS) ||
    (carsType === 'amp' && TabsPanelLabels.AMP_CARS) ||
    (carsType === 'electric' && TabsPanelLabels.ELECTRIC_CARS) ||
    (carsType === 'commercial' && TabsPanelLabels.COMMERCIAL_CARS) ||
    (carsType === 'hybrid' && TabsPanelLabels.HYBRID_CARS) ||
    TabsPanelLabels.ALL_CARS;

  const { die } = useCarSelectionHelper();

  return {
    activeTab,
    tabs: [
      allCarsEnabled
        ? { label: TabsPanelLabels.ALL_CARS, link: TabsPanelLinks.ALL_CARS }
        : null,
      newCarsEnabled
        ? { label: TabsPanelLabels.NEW_CARS, link: TabsPanelLinks.NEW_CARS }
        : null,
      ampCarsEnabled
        ? {
            label: TabsPanelLabels.AMP_CARS,
            link: TabsPanelLinks.AMP_CARS,
          }
        : null,
      electricCarsEnabled
        ? {
            label: TabsPanelLabels.ELECTRIC_CARS,
            link: TabsPanelLinks.ELECTRIC_CARS,
          }
        : null,
      hybridCarsEnabled
        ? {
            label: TabsPanelLabels.HYBRID_CARS,
            link: TabsPanelLinks.HYBRID_CARS,
          }
        : null,
      commercialCarsEnabled
        ? {
            label: TabsPanelLabels.COMMERCIAL_CARS,
            link: TabsPanelLinks.COMMERCIAL_CARS,
          }
        : null,
    ].filter(isNotNullish),
    die,
  };
}

export const useCarsStockSeo = (options?: {
  productMicromarking?: {
    ratingOff?: boolean;
  };
}) => {
  const router = useRouter();
  const dealers = useTypedSelector(selectDealers);
  const seoTemplate = useTypedSelector(selectSeoTemplate);
  const stockCarsData = useTypedSelector(getStockCarList);
  const selectedFilters = useTypedSelector(getSelectedFilters);
  const filterParams = useTypedSelector(getStockFilterParams);
  const organization = useSettingItem('ORGANIZATION_NAME');
  const address = useSettingItem('MAIN_ADDRESS');
  const url = usePageUrl();

  const { minPrice, maxPrice } = getStockCarsMinAndMaxPrices(
    stockCarsData?.items ?? []
  );

  const { rating, ratingCount } =
    getAverageRatingAndCountReviewsDealers(dealers);

  const seoPageParams = {
    h1: getSeoTemplateText(
      convertSeoField(seoTemplate?.h1 ?? '', selectedFilters, filterParams),
      ''
    ),
    title: convertSeoField(
      seoTemplate?.title ?? '',
      selectedFilters,
      filterParams
    ),
    description: convertSeoField(
      seoTemplate?.description ?? '',
      selectedFilters,
      filterParams
    ),
    openGraphImage: seoTemplate?.openGraphImage ?? '',
    keywords: seoTemplate?.keywords ?? '',
    canonicalUrl: router.asPath.replace(/(\/page|--page).*/, ''),
  };

  const productMicromarkingData = {
    h1: seoPageParams.title,
    minPrice,
    maxPrice,
    rating,
    ratingCount,
  };

  const eventMicromarkingNames = [
    '✔️ Большой выбор',
    '🔥 Онлайн-покупка',
    '🔥 Тест-драйв',
  ];
  const eventMicromarkingData = eventMicromarkingNames.map((name) => ({
    image: seoPageParams.openGraphImage ?? '',
    name,
    description: seoPageParams.description,
    startDate: getFirstDateOfYear(),
    endDate: getLastDateOfYear(),
    url,
    organization: organization ?? '',
    address: address ?? '',
  }));

  return {
    ...seoPageParams,
    productMicromarking: getStockAndCatalogProductMicromarking(
      productMicromarkingData,
      {
        ratingOff: options?.productMicromarking?.ratingOff,
      }
    ),
    eventMicromarking: eventMicromarkingData.map((data) =>
      getEventMicromarking(data)
    ),
  };
};
